/**
 * The pager. When a guest's order flips to READY, this is what makes them look
 * up.
 *
 * Cafés hand out physical pager pucks for exactly this problem, and those pucks
 * cost money, get lost, get stolen, need charging, and know nothing about who is
 * holding them. This replaces them with the phone already in the guest's hand —
 * and unlike a puck, the thing that buzzes is attached to an identified customer
 * with a points balance. That is the selling point, so the alert has to be as
 * good as the hardware it is displacing: a puck vibrates and flashes, and a
 * silent toast on a screen nobody is looking at does not compete with that.
 *
 * FOUR LAYERS, deliberately, because no single one works everywhere:
 *
 *   sound        — the broadest. Works on every browser IF the audio context was
 *                  unlocked by a real user gesture first (see arm()).
 *   vibration    — Android only. `navigator.vibrate` does not exist on iOS
 *                  Safari at all, in any context. Not a bug to chase.
 *   notification — Android/desktop, and iOS only for an INSTALLED PWA on 16.4+.
 *                  Fired only when the tab is hidden; on a visible tab the page
 *                  itself is already shouting and a banner is just noise.
 *   visual       — the only layer that is universal, so it is the one that must
 *                  never be skipped. Runs on iOS Safari where the other three
 *                  degrade to sound alone.
 *
 * Fires ONCE per order, tracked in localStorage: a guest who reloads the tracker
 * on a ready order should see the ready state, not be paged again.
 */

/** Orders already paged. Survives a reload; scoped per browser, not per tab. */
const ALERTED_KEY = "pp_ready_alerted";
/** Keep the list from growing forever on a shared café tablet. */
const ALERTED_MAX = 50;

let audioCtx = null;
let armed = false;

/* ── the alerted set ─────────────────────────────────────────────────────── */

function readAlerted() {
  try {
    const raw = JSON.parse(localStorage.getItem(ALERTED_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch (_) {
    return [];
  }
}

/** Has this order already paged the guest? */
export function alreadyAlerted(orderId) {
  return !!orderId && readAlerted().includes(orderId);
}

function markAlerted(orderId) {
  try {
    const next = [orderId, ...readAlerted().filter((id) => id !== orderId)].slice(0, ALERTED_MAX);
    localStorage.setItem(ALERTED_KEY, JSON.stringify(next));
  } catch (_) { /* private mode — the in-page guard still holds for this tab */ }
}

/* ── arming ──────────────────────────────────────────────────────────────── */

/**
 * Unlock the alert on a REAL USER GESTURE. Call this synchronously from a tap
 * handler — "Send to kitchen" is the natural one, and on iOS it is the only one
 * we are guaranteed to get.
 *
 * Browsers refuse to start audio that no human asked for, and the refusal is
 * silent: without this the chime would simply never play, and it would look like
 * the feature was working right up until the moment it mattered. Creating the
 * context inside the gesture, and playing one inaudible sample through it, is
 * what marks it as user-initiated for the rest of the session.
 *
 * Notification permission is requested here too, for the same reason — Safari
 * requires a user gesture for that prompt — and deliberately not awaited: a
 * guest who dismisses or ignores it must still get sound, vibration and the
 * visual, and the order must still send while the prompt is on screen.
 */
export function arm() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      audioCtx = audioCtx || new AC();
      if (audioCtx.state === "suspended") audioCtx.resume();
      // An inaudible blip. Some browsers only consider a context truly unlocked
      // once something has actually been played through it.
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain).connect(audioCtx.destination);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.01);
      armed = true;
    }
  } catch (_) { /* no audio on this device — the other layers still run */ }

  try {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  } catch (_) { /* not supported, or blocked by policy */ }
}

/** Whether the chime can be expected to play — the UI promises accordingly. */
export function isArmed() {
  return armed;
}

/* ── the alert ───────────────────────────────────────────────────────────── */

/**
 * A pager, not a notification chime: three rising tones, twice, ~1.4s of noise.
 *
 * Deliberately longer and more insistent than the kitchen's two-note bell. The
 * kitchen bell is heard by someone standing at the screen waiting for it; this
 * has to reach a guest mid-conversation with their phone face-down on a café
 * table, competing with a coffee grinder.
 */
function chime() {
  if (!audioCtx) return;
  try {
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const notes = [
      [0.00, 880], [0.18, 1174], [0.36, 1568],
      [0.72, 880], [0.90, 1174], [1.08, 1568],
    ];
    notes.forEach(([at, hz]) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.4, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.26);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.28);
    });
  } catch (_) {}
}

function buzz() {
  try {
    // Long-short-long-short-long — a pattern, not a single pulse, so it reads as
    // "come and get it" rather than an incoming message.
    navigator.vibrate?.([300, 120, 300, 120, 500]);
  } catch (_) {}
}

function notify(title, body, tag) {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    // Only when they are NOT looking. On a visible tab the page is already
    // flashing and chiming; a banner on top of that is noise, and on desktop it
    // covers the very thing it is announcing.
    if (!document.hidden) return;
    // `tag` collapses repeats of the same order into one banner rather than
    // stacking them.
    new Notification(title, { body, tag, renotify: true });
  } catch (_) {}
}

/**
 * Page the guest. Safe to call from more than one surface — the tracker and the
 * customer card can both be open, and the per-order guard means only the first
 * one through actually rings.
 *
 * @returns {boolean} whether this call was the one that fired.
 */
export function fire({ orderId, title, body }) {
  if (!orderId || alreadyAlerted(orderId)) return false;
  markAlerted(orderId);

  chime();
  buzz();
  notify(title, body, `pp-order-${orderId}`);

  return true;
}

const readyAlert = { arm, isArmed, fire, alreadyAlerted };
export default readyAlert;
